<template>
    <div data-testid="export-data-container">
        <div
            class="menu-action_item"
            data-testid="export-button-excel"
            @click="triggerExportDialog(exportTo.XLSX)"
        >
            <div class="flex items-center">
                <i class="vc-icon-download icon-md text-primary" />
                <p class="text-md mx-1">
                    {{ $t('general.export.excel') }}
                </p>
            </div>
        </div>
        <div
            class="menu-action_item"
            data-testid="export-button-csv"
            @click="triggerExportDialog(exportTo.CSV)"
        >
            <div class="flex items-center">
                <i class="vc-icon-download icon-md text-primary" />
                <p class="text-md mx-1">
                    {{ $t('general.export.csv') }}
                </p>
            </div>
        </div>
        <modal
            v-if="showExportDialog"
            :visible="showExportDialog"
            :append-to-body="true"
            data-testid="export-modal"
            @update:visible="($evt) => showExportDialog = $evt"
        >
            <div class="flex flex-row items-center pb-4">
                <h3
                    slot="title"
                    class="text-main-2xl font-semibold main-text-color"
                    data-testid="export-modal-title"
                >
                    {{ $t('general.exportData') }}
                </h3>
                <static-widget-info
                    :widget="widget"
                    class="px-2"
                />
            </div>
            <div class="pb-4">
                <label class="main-text-color">{{ $t('widget.export.fileName') }}</label>
                <el-input v-model="fileName" data-testid="export-input-filename" />
            </div>
            <el-alert
                :title="$t('widget.export.exportDataTo') +' '+format"
                type="info"
                :description="$t('widget.export.exportFileColumnOrder')"
                :closable="false"
                show-icon
            />
            <div class="py-6 border-b flex">
                <el-radio
                    v-model="exportFormat"
                    :label="exportTo.CSV"
                >
                    {{ $t('general.export.csv') }}
                </el-radio>
                <el-radio
                    v-model="exportFormat"
                    :label="exportTo.XLSX"
                >
                    {{ $t('general.export.excel') }}
                </el-radio>
            </div>
            <slot :on-manage-export="onManageExport" />
            <template #footer>
                <div class="border-t-2 border-gray-300 py-4 px-10 flex items-center justify-between main-bg-light">
                    <cancel-button
                        @on-click="showExportDialog = false"
                    />
                    <el-button
                        :disabled="loading || !columnsToExport.length"
                        :loading="loading"
                        type="primary"
                        @click="exportTableData"
                    >
                        {{ $t('widget.export.export') }}
                    </el-button>
                </div>
            </template>
        </modal>
    </div>
</template>
<script>
import XLSX from 'xlsx'
import { dayjs } from '@/composables/useDayjs'
import { Alert, Radio } from 'element-ui'
import DownloadIcon from 'vue-feather-icons/icons/DownloadIcon'
import { getWidgetDateInterval, isARealtimeTableWidget } from '@/helpers/widgetUtils'
import { DATE_TIME_COLUMNS, DATE_FORMAT, DATE_TIME_FORMAT } from '@/helpers/table'
import tableColumnsMixin from '@/mixins/tableColumnsMixin'
import { getTranslationOrUndefined } from '@/helpers/translation'
import startCase from 'lodash/startCase'
import get from 'lodash/get'

export default {
    inheritAttrs: false,
    name: 'ExportData',
    mixins: [ tableColumnsMixin ],
    components: {
        [Alert.name]: Alert,
        [Radio.name]: Radio,
        StaticWidgetInfo: () => import('@/components/Widgets/WidgetUpdateForm/StaticWidgetInfo.vue'),
        ManageColumns: () => import('./ManageColumns.vue'),
        DownloadIcon,
        Modal: () => import('@/components/Common/Modal.vue'),
        CancelButton: () => import('@/components/Common/Buttons/CancelButton.vue')
    },
    props: {
        tableId: {
            type: String,
            default: '',
        },
        widgetTitle: {
            type: String,
            default: '- -',
        },
        allRecords: {
            type: Array,
            default: () => [],
        },
        widget: {
            type: Object,
            default: () => ({}),
        },
        columnsToExport: {
            type: Array,
            default: () => [],
        },
    },
    data () {
        return {
            onManageExport: true,
            exportTo: {
                XLSX: '.xlsx',
                CSV: '.csv',
            },
            exportToDictionary: {
                '.xlsx': 'Excel',
                '.csv': 'CSV',
            },
            exportFormat: '',
            showExportDialog: false,
            loading: false,
            fileName: '--',
        }
    },
    computed: {
        format () {
            return this.$t(this.exportToDictionary[this.exportFormat])
        },
    },
    methods: {
        triggerExportDialog (exportFormat) {
            this.showExportDialog = true
            this.exportFormat = exportFormat
            this.fileName = this.$t(this.widgetTitle)
        },
        getFileName () {
            const fileName = this.fileName || this.$t('widget.title')
            const timeRange = getWidgetDateInterval(this.widget, 'DD-MM-YYYY')

            return fileName + ' ' + timeRange + this.exportFormat
        },
        exportTableData () {
            this.loading = true
            const fileName = this.getFileName()

            if (isARealtimeTableWidget(this.widget)) {
                this.$emit('on-update-layout', true)
                this.showExportDialog = false

                const tableId = this.tableId

                const tableElement = document.getElementById(tableId)
                const excelWorkBook = XLSX.utils.table_to_book(tableElement)

                XLSX.writeFile(excelWorkBook, fileName)

                this.closeModal()

                return
            }

            // As sorting of columns goes with the order of the objects in the Column.availableColumns array but columnsToExport is an array of prop names which are not affected by the order, need to sort the columnsToExport array based on the order of the availableColumns array
            const columnsToExportSet = new Set(this.columnsToExport)
            const columnsToExport = []

            for (const obj of get(this.widget, 'WidgetLayout.Columns.availableColumns', [])) {
                if (columnsToExportSet.has(obj.prop)) {
                    columnsToExport.push(obj.prop)
                }
            }

            const records = []

            this.allRecords.forEach((record) => {
                const data = {}
                columnsToExport.forEach(key => {
                    let templateColumnTranslation = undefined

                    if (this.widget.WidgetTemplateColumns.length) {
                        const keyColumn = this.widget.WidgetTemplateColumns.find(column => column.WidgetTemplateColumnsName === key)
                        if (keyColumn) {
                            templateColumnTranslation = getTranslationOrUndefined(keyColumn.TemplateNameContentTag)
                                || getTranslationOrUndefined(keyColumn.WidgetTemplateColumnsName)
                                || startCase(keyColumn.WidgetTemplateColumnsName)
                        }
                    }

                    const translation = this.replaceDefaultLoginStatusNameWithAccountBreakName(templateColumnTranslation) || this.makeTableHeaderTranslationsByProp(key)
                    data[translation] = this.tryFormatCellTypeDate(record, key)
                })
                records.push(data)
            })

            const data = XLSX.utils.json_to_sheet(records)
            const excelWorkBook = XLSX.utils.book_new()

            XLSX.utils.book_append_sheet(excelWorkBook, data, 'data')
            XLSX.writeFile(excelWorkBook, fileName)

            this.$emit('on-update-layout', true)
            this.closeModal()
        },
        tryFormatCellTypeDate (record, key) {
            if (!record[key]) {
                return ''
            }
            if (DATE_FORMAT.includes(key.toLowerCase())) {
                try {
                    // To prevent dayjs errors like: Invalid time value
                    record[key] = record[key].replace('Z', '')
                    return dayjs(new Date(record[key])).format(DATE_FORMAT)
                } catch (e) {
                    return record[key].replace(/\//g, '-')
                }
            }
            if (DATE_TIME_COLUMNS.includes(key.toLowerCase())) {
                try {
                    // To prevent dayjs errors like: Invalid time value
                    record[key] = record[key].replace('Z', '')
                    return dayjs(new Date(record[key])).format(DATE_TIME_FORMAT)
                } catch (e) {
                    return record[key].replace(/\//g, '-')
                }
            }
            return record[key]
        },
        closeModal () {
            this.loading = false
            this.showExportDialog = false
        }
    },
}
</script>

<style lang="scss" scoped>
.menu-action_item {
    @apply w-full flex items-center p-2 cursor-pointer;
    color: var(--mainTextColor-color);
    &:hover {
        color: var(--primary-color);
    }
}
::v-deep .el-radio__inner {
    background-color: var(--mainBgColor-color);
}
::v-deep .el-radio__label {
    color: var(--mainTextColor-color);
}
::v-deep .el-alert--info.is-light {
    background-color: var(--mainBgColor-color);
    color: var(--mainTextColor-color);
}
::v-deep .el-alert__description {
    color: var(--mainTextColor-color);
}
</style>
